// Shiny → React bridge for live engine-output streaming.
//
// On every slider change, Shiny's reactive cycle re-runs simulate_reform()
// and pushes the result here via session$sendCustomMessage(). The hook
// below subscribes and writes the values into the Zustand store, so any
// React component reading from the store re-renders with the new numbers.
//
// Why a custom-message channel rather than fetching from Plumber on every
// slider change: Shiny *already* computes the output (the existing Shiny
// dashboard needs it), so re-issuing the same compute via Plumber would
// double the work. Plumber stays for queries the React side initiates
// (baseline on mount, compare-reforms in M2). Custom-message is for live
// "state Shiny computed anyway" streaming.
//
// Message channel: "beamm:engine_output"
//   payload: { engineOutput, baselineOutput, isModified }

import { useEffect } from "react";
import { useResultsStore } from "./results_store";
import { setSessionToken } from "./session_token";
import type { EngineOutput } from "../lib/types";
import "../lib/shiny";

interface EnginePayload {
  engineOutput: EngineOutput;
  baselineOutput: EngineOutput;
  isModified: boolean;
}

/** Subscribe to live engine output from Shiny. Call once near the island root. */
export function useShinyEngineBridge(): void {
  const setEngineOutput = useResultsStore((s) => s.setEngineOutput);
  const setBaselineOutput = useResultsStore((s) => s.setBaselineOutput);
  const setIsModified = useResultsStore((s) => s.setIsModified);
  const setSimulating = useResultsStore((s) => s.setSimulating);
  const setEngineError = useResultsStore((s) => s.setEngineError);

  useEffect(() => {
    if (typeof window === "undefined" || !window.Shiny?.addCustomMessageHandler) {
      return;
    }
    const handler = (payload: unknown): void => {
      const p = payload as EnginePayload;
      if (p.engineOutput) setEngineOutput(p.engineOutput);
      if (p.baselineOutput) setBaselineOutput(p.baselineOutput);
      if (typeof p.isModified === "boolean") setIsModified(p.isModified);
      // Engine output arriving means the commit-gated compute finished —
      // dismiss the "Simulating…" overlay. Shiny can register only one
      // handler per message type, so this is the single dismiss point;
      // the matching show lives in state/simulating_signal.ts.
      setSimulating(false);
    };
    window.Shiny.addCustomMessageHandler("beamm:engine_output", handler);
    // Same channel, registered here because this is the one place already
    // wired to window.Shiny: store the session token the server pushes, so
    // api.ts can stamp it on the Plumber requests for cross-process log
    // correlation (#194).
    window.Shiny.addCustomMessageHandler("beamm:session", (token: unknown) => {
      if (typeof token === "string") setSessionToken(token);
    });
    // A reform that failed server-side: dismiss the "Simulating…" overlay at
    // once (don't wait out the safety timeout) and surface the message. The
    // previous results stay on screen under the banner; the next successful
    // output clears it (setEngineOutput).
    window.Shiny.addCustomMessageHandler("beamm:engine_error", (e: unknown) => {
      const message = (e as { message?: string })?.message;
      setSimulating(false);
      // The server always sends a user-facing message; this generic fallback
      // only covers a malformed/empty payload.
      setEngineError(message ?? "Something went wrong — please try again.");
    });
    // Shiny doesn't expose a removeCustomMessageHandler — handlers are
    // registered for the session's lifetime. That's fine for our island
    // which mounts once and lives for the session.
  }, [
    setEngineOutput,
    setBaselineOutput,
    setIsModified,
    setSimulating,
    setEngineError,
  ]);
}
